const config = require('../core/config');
const { Keywords } = require('../core/keywords');

/**
 * Creates a syntax highlighter for Bhasa code
 * Uses language-specific keywords for highlighting
 */
class SyntaxHighlighter {
    constructor() {
        this.langConfig = null;
        this.keywordPatterns = [];
    }

    /**
     * Set the language for highlighting
     * @param {string} language - Language name (english, hindi, etc.)
     * @param {string} name - User's name for personalizing keywords
     */
    setLanguage(language, name) {
        const savedConfigStr = localStorage.getItem(`bhasa_config_${language}`);
        let customKeywords = {};

        if (savedConfigStr) {
            try {
                customKeywords = JSON.parse(savedConfigStr);
            } catch (e) {
                console.error("Error parsing saved config for highlighter", e);
            }
        }

        this.langConfig = config.getResolvedKeywords(language, name, customKeywords);
        this.buildKeywordPatterns();
    }

    /**
     * Build regex patterns for all keywords
     * Sort by length (longest first) to match multi-word keywords correctly
     */
    buildKeywordPatterns() {
        this.keywordPatterns = [];

        if (!this.langConfig) return;

        // Collect all keyword values and sort by length (longest first)
        const keywordList = Object.values(Keywords)
            .map(key => this.langConfig[key])
            .filter(Boolean)
            .sort((a, b) => b.length - a.length);

        this.keywordPatterns = keywordList.map(kw => ({
            pattern: this.escapeRegex(kw),
            keyword: kw
        }));
    }

    /**
     * Escape special regex characters
     */
    escapeRegex(str) {
        return str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    }

    /**
     * Escape HTML special characters
     */
    escapeHtml(text) {
        const htmlEntities = {
            '&': '&amp;',
            '<': '&lt;',
            '>': '&gt;',
            '"': '&quot;',
            "'": '&#39;'
        };
        return text.replace(/[&<>"']/g, char => htmlEntities[char]);
    }

    /**
     * Highlight code and return HTML
     * @param {string} code - Source code to highlight
     * @param {string} searchTerm - Optional term to highlight
     * @param {boolean} caseSensitive - Whether search is case sensitive
     * @returns {string} - HTML with syntax highlighting spans
     */
    highlight(code, searchTerm = '', caseSensitive = false) {
        if (!code) return '';
        if (!this.langConfig) return this.escapeHtml(code);

        // Tokenize and highlight
        const result = this.tokenize(code, searchTerm, caseSensitive);
        return result;
    }

    /**
     * Tokenize and highlight the code
     */
    tokenize(code, searchTerm = '', caseSensitive = false) {
        const tokens = [];
        let remaining = code;
        let position = 0;

        while (remaining.length > 0) {
            let matched = false;

            // Try to match a string (double or single quotes)
            const stringMatch = remaining.match(/^"([^"\\]|\\.)*"|^'([^'\\]|\\.)*'/);
            if (stringMatch) {
                const stringVal = stringMatch[0];
                // Sub-tokenize the string for escape sequences
                const escapeRegex = /\\([nrtbf\\'"])/g;
                let lastIdx = 0;
                let match;
                const stringParts = [];

                while ((match = escapeRegex.exec(stringVal)) !== null) {
                    // Plain part before escape
                    if (match.index > lastIdx) {
                        stringParts.push({
                            type: 'string-content',
                            value: stringVal.substring(lastIdx, match.index)
                        });
                    }
                    // Escape sequence
                    stringParts.push({
                        type: 'escape-sequence',
                        value: match[0]
                    });
                    lastIdx = escapeRegex.lastIndex;
                }

                if (lastIdx < stringVal.length) {
                    stringParts.push({
                        type: 'string-content',
                        value: stringVal.substring(lastIdx)
                    });
                }

                tokens.push({
                    type: 'string-wrapper',
                    parts: stringParts
                });

                remaining = remaining.slice(stringVal.length);
                position += stringVal.length;
                matched = true;
                continue;
            }

            // Try to match a comment
            const singleLineCommentMatch = remaining.match(/^\/\/.*/);
            if (singleLineCommentMatch) {
                tokens.push({
                    type: 'comment',
                    value: singleLineCommentMatch[0]
                });
                remaining = remaining.slice(singleLineCommentMatch[0].length);
                position += singleLineCommentMatch[0].length;
                matched = true;
                continue;
            }

            const multiLineCommentMatch = remaining.match(/^\/\*[\s\S]*?\*\//);
            if (multiLineCommentMatch) {
                tokens.push({
                    type: 'comment',
                    value: multiLineCommentMatch[0]
                });
                remaining = remaining.slice(multiLineCommentMatch[0].length);
                position += multiLineCommentMatch[0].length;
                matched = true;
                continue;
            }

            // Try to match a number
            const numberMatch = remaining.match(/^\d+(\.\d+)?/);
            if (numberMatch) {
                tokens.push({
                    type: 'number',
                    value: numberMatch[0]
                });
                remaining = remaining.slice(numberMatch[0].length);
                position += numberMatch[0].length;
                matched = true;
                continue;
            }

            // Try to match keywords (longest first)
            for (const { pattern, keyword } of this.keywordPatterns) {
                const keywordRegex = new RegExp(`^${pattern}(?![\\w\\u0900-\\u097F\\u0980-\\u09FF])`);
                const keywordMatch = remaining.match(keywordRegex);
                if (keywordMatch) {
                    tokens.push({
                        type: 'keyword',
                        value: keywordMatch[0]
                    });
                    remaining = remaining.slice(keywordMatch[0].length);
                    position += keywordMatch[0].length;
                    matched = true;
                    break;
                }
            }
            if (matched) continue;

            // Try to match operators
            const operatorMatch = remaining.match(/^(==|!=|<=|>=|&&|\|\||[+\-*/%<>=!])/);
            if (operatorMatch) {
                tokens.push({
                    type: 'operator',
                    value: operatorMatch[0]
                });
                remaining = remaining.slice(operatorMatch[0].length);
                position += operatorMatch[0].length;
                matched = true;
                continue;
            }

            // Try to match brackets
            const bracketMatch = remaining.match(/^[\[\](){}]/);
            if (bracketMatch) {
                tokens.push({
                    type: 'bracket',
                    value: bracketMatch[0]
                });
                remaining = remaining.slice(1);
                position += 1;
                matched = true;
                continue;
            }

            // No match - consume one character as plain text
            tokens.push({
                type: 'plain',
                value: remaining[0]
            });
            remaining = remaining.slice(1);
            position += 1;
        }

        const highlightSearch = (text) => {
            if (!searchTerm) return this.escapeHtml(text);
            const escapedText = this.escapeHtml(text);
            const escapedSearch = this.escapeHtml(searchTerm);
            const flags = caseSensitive ? 'g' : 'gi';
            const regex = new RegExp(this.escapeRegex(escapedSearch), flags);
            return escapedText.replace(regex, match => `<span class="search-highlight">${match}</span>`);
        };

        // Build HTML from tokens
        return tokens.map(token => {
            if (token.type === 'string-wrapper') {
                return `<span class="syntax-string">${token.parts.map(part => {
                    const escaped = highlightSearch(part.value);
                    if (part.type === 'escape-sequence') {
                        return `<span class="syntax-escape">${escaped}</span>`;
                    }
                    return escaped;
                }).join('')}</span>`;
            }

            const highlighted = highlightSearch(token.value);
            switch (token.type) {
                case 'keyword':
                    return `<span class="syntax-keyword">${highlighted}</span>`;
                case 'string':
                    return `<span class="syntax-string">${highlighted}</span>`;
                case 'number':
                    return `<span class="syntax-number">${highlighted}</span>`;
                case 'operator':
                    return `<span class="syntax-operator">${highlighted}</span>`;
                case 'bracket':
                    return `<span class="syntax-bracket">${highlighted}</span>`;
                case 'comment':
                    return `<span class="syntax-comment">${highlighted}</span>`;
                default:
                    return highlighted;
            }
        }).join('');
    }
}

module.exports = SyntaxHighlighter;
